/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import { Box, Button, ButtonBase, Typography } from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import { HEADER_HEIGHT } from '../../shared/typings';
import { Call, CallDisconnectStatus, CallStatus } from './typings';
import CallChip from './components/CallChip';
import { useSetAtom } from 'jotai';
import { callQueueBannerVisibleAtom } from './states';
import MoreCallsPopover from './components/MoreCallsPopover';
import { IconWrap, PulseRing } from './styles';
import { ReactComponent as CallIncomingIcon } from '../../shared/assets/svg/call-incoming.svg';

type CallQueueBannerProps = {
  calls: Array<Call & { callStartTime?: number }>;
  activeCallId?: string | null;
  onSelect: (call: Call) => void;
  onMinimize?: (call: Call) => void;
  onCloseChip?: (call: Call) => void;
  onDisconnect: (status: CallDisconnectStatus, call: Call, removeWithoutDelay?: boolean) => void;
  onAcceptStateChange: (call: Call, state: CallStatus) => void;
  onRequestAccept: (call: Call, switchCall?: boolean) => void;
};

const MAX_VISIBLE_CALLS = 4; // Maximum number of calls to show in the banner

const CallQueueBanner = ({
  calls,
  activeCallId,
  onSelect,
  onMinimize,
  onCloseChip,
  onDisconnect,
  onAcceptStateChange,
  onRequestAccept,
}: CallQueueBannerProps) => {
  const setBannerVisible = useSetAtom(callQueueBannerVisibleAtom);
  const [moreCallsAnchorEl, setMoreCallsAnchorEl] = useState<HTMLElement | null>(null);

  const isOneActiveCall = useMemo(
    () => Boolean(calls.length === 1 && activeCallId && activeCallId === calls[0].callId),
    [calls.length, activeCallId]
  );
  // Do not play ringtone if any call is already connecting/connected
  const isSystemBusy = calls.some(
    (c) => c.status === CallStatus.IN_PROGRESS || c.status === CallStatus.Connecting || c.status === CallStatus.Ringing
  );
  const shouldRender = !!calls && calls.length > 0;
  const isBannerVisible = shouldRender && !isOneActiveCall;

  // Split calls into visible and hidden
  const visibleCalls = useMemo(() => calls.slice(0, MAX_VISIBLE_CALLS), [calls]);
  const hiddenCalls = useMemo(() => calls.slice(MAX_VISIBLE_CALLS), [calls]);

  // get the count of calls by status
  const missedCallsCount = useMemo(() => calls.filter((c) => c.status === CallStatus.MISSED).length, [calls]);
  const incomingCallsCount = useMemo(() => calls.filter((c) => c.status === CallStatus.INITIATED).length, [calls]);

  useEffect(() => {
    setBannerVisible(isBannerVisible);
    return () => setBannerVisible(false);
  }, [isBannerVisible, setBannerVisible]);

  if (!shouldRender) return null;
  return (
    <Box
      css={css`
        background-color: #f0f5f0;
        display: flex;
        flex-direction: row-reverse;
        align-items: flex-start;
        gap: 12px;
        overflow: visible;
        white-space: nowrap;
        position: relative;
        z-index: 2;
        &::-webkit-scrollbar {
          display: none;
        }
        -ms-overflow-style: none;
        scrollbar-width: none;

        // since we need to hide the queue bar if there is one call and that too is active and still render the chip :
        height: ${isOneActiveCall ? 0 : HEADER_HEIGHT}px;
        border-bottom: ${isOneActiveCall ? 'none' : '1px solid #e0e4e7'};
        padding: ${isOneActiveCall ? '0px' : '8px 16px'};
        transition: height 300ms ease-in-out, padding-block 300ms ease-in-out;
      `}
    >
      {visibleCalls.map((call, index) => (
        <CallChip
          key={call.tempCallId || call.callId}
          call={call}
          isActive={activeCallId === call.callId}
          onSelect={onSelect}
          onMinimize={onMinimize}
          onCloseChip={onCloseChip || (() => {})}
          onDisconnect={onDisconnect}
          onRequestAccept={onRequestAccept}
          onAcceptStateChange={(call, state) => onAcceptStateChange(call, state)}
          callIndex={index}
          // Only allow ringtone when the system is not busy (no connecting/connected calls)
          // and only for the active call at end (or first call when none active)
          shouldPlayRingtone={!isSystemBusy && (activeCallId ? activeCallId === call.callId : index === 0)}
          isAnyCallActive={!!activeCallId}
        />
      ))}

      {hiddenCalls.length > 0 && (
        <>
          <ButtonBase
            onClick={(e) => setMoreCallsAnchorEl(e.currentTarget)}
            sx={{
              height: '40px',
              backgroundColor: '#F0F5F0',
              border: '1px solid #DADAE4',
              gap: '10px',
              position: 'relative',
              display: 'inline-flex',
              alignItems: 'center',
              padding: '8px 16px 8px 12px',
              borderRadius: '38px',
              boxShadow: '0 0 0 0 rgba(0,0,0,0)',
              overflow: 'hidden',
              WebkitTapHighlightColor: 'transparent',
            }}
          >
            <Box position="relative">
              <IconWrap
                aria-hidden="true"
                sx={{
                  color: '#3D906D',
                }}
              >
                <CallIncomingIcon width={16} height={16} />
              </IconWrap>
              <PulseRing delay={0} />
              <PulseRing delay={250} />
              <PulseRing delay={500} />
            </Box>
            +{hiddenCalls.length}
          </ButtonBase>

          <MoreCallsPopover
            anchorEl={moreCallsAnchorEl}
            onClose={() => setMoreCallsAnchorEl(null)}
            calls={hiddenCalls}
            activeCallId={activeCallId}
            onSelect={(call) => {
              onSelect(call);
              setMoreCallsAnchorEl(null);
            }}
            onMinimize={onMinimize}
            onCloseChip={onCloseChip}
            onDisconnect={onDisconnect}
            onAcceptStateChange={onAcceptStateChange}
            onRequestAccept={onRequestAccept}
          />
        </>
      )}

      {/* call counts, 1 missed • 2 incoming */}
      {/* <Box
        css={css`
          margin-right: auto;
          display: flex;
          flex-direction: row;
          align-items: center;
          gap: 4px;
        `}
      >
        <Typography variant="body2" sx={{ fontWeight: 500, color: '#2d2d2d', lineHeight: 1, fontSize: 14, whiteSpace: 'nowrap'}}>
          {missedCallsCount} missed
        </Typography>
        <Typography variant="body2" sx={{ fontWeight: 500, color: '#2d2d2d', lineHeight: 1, fontSize: 14, whiteSpace: 'nowrap'}}>
          •
        </Typography>
        <Typography variant="body2" sx={{ fontWeight: 500, color: '#2d2d2d', lineHeight: 1, fontSize: 14, whiteSpace: 'nowrap'}}>
          {incomingCallsCount} incoming
        </Typography>
      </Box> */}
    </Box>
  );
};

export default CallQueueBanner;
